import React, { ReactElement } from 'react';
import { useSetMyPinMutation } from 'apollo/generated/client-operations';
import { useIsMounted } from 'utils';
import { getGraphQLError } from 'utils/error';
import { FormOnSubmit } from 'components/form';
import { AlertService } from 'components/alertService';
import { ModalFormContentWithBackButton } from 'components/form/ModalFormContentWithBackButton';
import {
  ConfirmPinFormData,
  getFormDefaultValues,
  getFormInputsHook,
} from './ConfirmPinStep.forms';

export type ConfirmPinStepProps = {
  closeDrawer: () => void;
  hasExistingPin: boolean;
  capturedPin: string;
  onBack: () => void;
  onPinSet: () => void;
};

export function ConfirmPinStep(props: ConfirmPinStepProps): ReactElement {
  const { closeDrawer, hasExistingPin, capturedPin, onBack, onPinSet } = props;
  const isMounted = useIsMounted();
  const [isValid, setIsValid] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [setMyPin] = useSetMyPinMutation();

  const inputs = React.useMemo(() => {
    return getFormInputsHook({ capturedPin, setIsValid });
  }, [capturedPin]);

  const defaultValues = React.useMemo(() => getFormDefaultValues(), []);

  const onSubmit: FormOnSubmit<ConfirmPinFormData> = async (
    data,
  ): Promise<void> => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await setMyPin({ variables: { input: { pin: data.pin } } });
      closeDrawer();
      AlertService.alert(
        'success',
        'Success',
        `PIN successfully ${hasExistingPin ? 'updated' : 'set'}`,
      );
      onPinSet();
    } catch (err) {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(err),
      );
    } finally {
      if (isMounted()) setIsSaving(false);
    }
  };

  return (
    <ModalFormContentWithBackButton
      setOpen={closeDrawer}
      inputs={inputs}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      actionText="Save"
      actionButtonEnabled={isValid && !isSaving}
      actionButtonWithSpinner={isSaving}
      onBack={onBack}
    />
  );
}
